import * as z from "zod";
import { cursorPagination, delegatedAccessKinds } from "../index";
import { preparedActionSubjects } from "../mcp";
import { type PreparedActionStatus, preparedActionStatuses } from "./enums";

// Prepared actions (ADR-265, spec v0.3 §8.O, D-29): an irreversible write an assistant or a token asked for through
// MCP. The tool only prepares it; its holder sees it in Backstage and confirms it, which runs the same operation of the
// app with the user's own Actor (ADR-039 point 2), or discards it. identity keeps them, as it keeps the accesses that
// prepare them; the route of Backstage that confirms runs the operation.

/** What an answer says of an action: the same words as the table, with a pending one past its end `expired`. */
export type PreparedActionState = PreparedActionStatus;

/** The longest arguments of an action, in characters of their JSON: a few hundred lines of a PO. */
export const PREPARED_ACTION_INPUT_MAX = 20_000;
/**
 * A confirmation that never ended (Backstage closed between the operation and its outcome) is claimed again after
 * this: the operation of the domain is idempotent by state, so a second run finds it done and refuses.
 */
export const PREPARED_ACTION_CLAIM_MINUTES = 5;
/** The page of Backstage with the actions of the signed-in user; the link a tool answers adds the id. */
export const preparedActionsPath = "/settings/actions";

const scalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const flat = z.record(z.string(), z.union([scalar, z.array(scalar)]));
/**
 * The arguments of a tool as its route validated them: values, lists, and lists of lines, three levels at most. Every
 * irreversible tool takes no more; a deeper one is refused before identity keeps it.
 */
export const preparedActionArguments = z.record(z.string(), z.union([scalar, flat, z.array(z.union([scalar, flat]))]));
export type PreparedActionArguments = z.output<typeof preparedActionArguments>;

const subject = z.object({
  type: z.enum(preparedActionSubjects),
  id: z.uuid(),
  /** What the user knows it by: a number (PO-…, SO-…), a name. */
  label: z.string().max(200),
});

// --- gateway-mcp ---

/** A tool of MCP prepares an action: the arguments as its route validated them, and the entity it acts on. */
export const prepareActionInput = z.object({
  tool: z.string().min(1).max(64),
  input: preparedActionArguments,
  subject: subject.nullable(),
});
export type PrepareActionInput = z.input<typeof prepareActionInput>;

/** One action, by its id: the holder in Backstage, or the access that prepared it. */
export const preparedActionParams = z.object({ actionId: z.uuid() });
export type PreparedActionParamsInput = z.input<typeof preparedActionParams>;

// --- Backstage ---

export const listMyPreparedActionsInput = z.object({
  /** `open`: pending, being confirmed or failed in the last day; `all`: every one, newest first. */
  state: z.enum(["open", "all"]).default("open"),
  ...cursorPagination({ defaultLimit: 25, maxLimit: 100 }),
});
export type ListMyPreparedActionsInput = z.input<typeof listMyPreparedActionsInput>;

/** How a confirmation ended: what the domain answered, from the route of Backstage that ran it. */
export const settlePreparedActionInput = preparedActionParams.extend({
  /** `ok`, or the code of the error of the domain. */
  outcome: z.string().min(1).max(100),
  message: z.string().max(500).nullable(),
  /** The domain did not answer (5xx): the action waits again, and the holder may confirm it later. */
  retry: z.boolean().default(false),
});
export type SettlePreparedActionInput = z.input<typeof settlePreparedActionInput>;

const access = z.object({
  id: z.uuid(),
  kind: z.enum(delegatedAccessKinds),
  /** The name of the token, or the client of the connection (`Claude`). */
  name: z.string(),
  /** Revoked since it prepared the action: Backstage no longer lets the holder confirm it. */
  revoked: z.boolean(),
});

/** A prepared action, as its holder and the access that prepared it see it. */
export const preparedActionDto = z
  .object({
    id: z.uuid(),
    /** The name of the tool (`confirm_purchase_order`): its title and its words are in `@nexxt/contracts/mcp`. */
    tool: z.string(),
    state: z.enum(preparedActionStatuses),
    /** The arguments, as the route validated them: what the confirmation runs. */
    input: preparedActionArguments,
    subject: subject.nullable(),
    access,
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    /** When it was confirmed, failed or discarded. */
    settledAt: z.iso.datetime().nullable(),
    /** What the domain answered: `ok`, or its error with the message. */
    outcome: z.object({ code: z.string(), message: z.string().nullable() }).nullable(),
    /** The page of Backstage where the holder confirms it; null where the environment has no address for the app. */
    confirmUrl: z.string().nullable(),
  })
  .meta({ id: "PreparedAction" });
export type PreparedActionDto = z.output<typeof preparedActionDto>;

/** What the route of Backstage runs once the holder claimed it: the tool, its arguments and who prepared it. */
export const claimedPreparedActionDto = z.object({
  id: z.uuid(),
  tool: z.string(),
  input: preparedActionArguments,
  access: access.omit({ revoked: true }),
});
export type ClaimedPreparedActionDto = z.output<typeof claimedPreparedActionDto>;

export type PreparedActionErrorCode =
  /** It is no longer waiting: confirmed, being confirmed, discarded, expired, or its access was revoked. */
  | "prepared_action_not_pending"
  /** A tool that prepares nothing, a tool the access does not open, or arguments too long. */
  | "prepared_action_invalid";
